/**
 * @file Floating agent surface: drag-positioned panel that hosts the
 * `MessagesList`, the `ModelInput` composer, and the open/close toggle for
 * the AI sidebar. Hidden entirely on a small set of sensitive settings
 * routes (`AGENT_HIDDEN_PATHS`). Bound to `AIContext` for actions and flags, to
 * `AITranscriptContext` for `messageCount` only — never the message array — and
 * to the user's location for route-aware visibility. Width, dock side and open
 * state come from `uiState.agentSidebar`, not from `AIContext`: they used to
 * travel through the context because `MappingReviewPanel` anchors itself off the
 * same live width, and that made a hover on any unrelated resize handle re-render
 * the whole agent. Both panels now read the store directly. The resize handle's
 * grip is transparent in every state by design — it lies over the card's shadow,
 * which is the affordance the panel has always used. The header row wraps, its
 * toggle labels never break, and each label shares a non-wrapping box with its
 * Switch, so a wrap never separates the two.
 */
import {
  DragEvent as ReactDragEvent,
  FC,
  useContext,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { Grid, Button, Switch, Typography, theme } from 'antd';
import { DeploymentUnitOutlined, CloseOutlined } from '@ant-design/icons';

import FlexBox from '../atoms/FlexBox';
import PanelResizeHandle from '../atoms/PanelResizeHandle';
import {
  clampPanelWidth,
  panelViewportClamp,
  uiState,
} from '../../store/uiState';
import MessagesList from './MessagesList';
import ModelInput from './ModelInput';
import FlexCard from '../atoms/FlexCard';
import { AIContext } from '../../providers/AIContext';
import { AITranscriptContext } from '../../providers/AITranscriptContext';

/**
 * Settings tabs where the agent surface is suppressed entirely: user profile,
 * org-wide config, security/SSO, API keys. These pages have no business with
 * the agent and one (API keys) is sensitive read material.
 */
const AGENT_HIDDEN_PATHS = new Set([
  '/settings/user',
  '/settings/organization',
  '/settings/security',
  '/settings/api',
]);

const Agent: FC = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const { pathname } = useLocation();
  const { messageCount } = useContext(AITranscriptContext);
  const {
    clearMessages,
    isLoading,
    agenticProgress,
    attachFiles,
    signifyAuthorized,
    setSignifyAuthorized,
    mcpAuthorized,
    setMcpAuthorized,
  } = useContext(AIContext);
  const {
    width: storedWidth,
    isOpen,
    position,
  } = useSnapshot(uiState.agentSidebar, { sync: true });
  const width = clampPanelWidth('agentSidebar', storedWidth);
  const dragCounterRef = useRef(0);

  if (AGENT_HIDDEN_PATHS.has(pathname)) return null;

  const isFileDrag = (e: ReactDragEvent<HTMLDivElement>) =>
    Array.from(e.dataTransfer.types ?? []).includes('Files');

  const onDragEnter = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!isOpen || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const onDragOver = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!isOpen || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragLeave = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!isOpen) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const onDrop = async (e: ReactDragEvent<HTMLDivElement>) => {
    if (!isOpen) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    await attachFiles(files);
  };

  const dropProps = { onDragEnter, onDragOver, onDragLeave, onDrop };

  const handleToggle = () => {
    uiState.agentSidebar.isOpen = !uiState.agentSidebar.isOpen;
  };

  const transition = 'transform 0.3s ease';
  const cardWidth = screens.md ? width : 'calc(100% - 40px)';

  return (
    <>
      <Button
        icon={<DeploymentUnitOutlined />}
        onClick={handleToggle}
        type={isOpen ? 'primary' : 'default'}
        size="small"
        style={{
          position: 'fixed',
          bottom: token.marginSM,
          right: token.marginSM,
          borderRadius: 20,
          paddingInline: token.paddingMD,
          zIndex: token.zIndexPopupBase - 1,
          boxShadow: token.boxShadowSecondary,
        }}
      >
        Agent
      </Button>
      <FlexCard
        alignEnd
        variant="outlined"
        {...dropProps}
        style={{
          position: 'fixed',
          [position]: 20,
          top: 20,
          width: cardWidth,
          maxWidth: panelViewportClamp('agentSidebar'),
          height: `calc(100vh - 150px)`,
          transition,
          transform: isOpen
            ? 'translateX(0)'
            : `translateX(${position === 'right' ? '120%' : '-120%'})`,
          zIndex: token.zIndexPopupBase + 1,
          boxShadow: token.boxShadow,
          outline: isDragOver ? `2px dashed ${token.colorPrimary}` : undefined,
          outlineOffset: isDragOver ? -2 : undefined,
          paddingTop: 0,
        }}
        styles={{
          body: {
            overflowY: 'auto',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 0,
          },
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 'calc(50% - 20px)',
            left: 2,
            height: 40,
            width: 2,
            borderRadius: 5,
            background: token.colorTextQuaternary,
          }}
        />
        <FlexBox
          noGrow
          wrap
          style={{
            position: 'sticky',
            top: 0,
            background: token.colorBgContainer,
            paddingTop: 10,
            paddingBottom: 2,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            zIndex: 25,
          }}
        >
          <Button type="text" size="small">
            {messageCount} message{messageCount !== 1 ? 's' : ''}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={clearMessages}
            aria-label="Clear messages"
            disabled={
              messageCount === 0 || isLoading || agenticProgress?.isRunning
            }
            style={{
              marginRight: 'auto',
            }}
          >
            clear
          </Button>
          <FlexBox noGrow>
            <Typography.Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}
            >
              Signify
            </Typography.Text>
            <Switch
              size="small"
              checked={signifyAuthorized}
              onChange={setSignifyAuthorized}
              disabled={isLoading || agenticProgress?.isRunning}
              aria-label="Allow Signify lookups"
            />
          </FlexBox>
          <FlexBox noGrow>
            <Typography.Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}
            >
              MCP
            </Typography.Text>
            <Switch
              size="small"
              checked={mcpAuthorized}
              onChange={setMcpAuthorized}
              disabled={isLoading || agenticProgress?.isRunning}
              aria-label="Allow MCP tool calls"
            />
          </FlexBox>
          <Button
            type="text"
            size={screens.md ? 'small' : 'middle'}
            icon={<CloseOutlined />}
            onClick={() => {
              uiState.agentSidebar.isOpen = false;
            }}
            aria-label="Close agent panel"
          />
        </FlexBox>
        {isOpen && <MessagesList />}
      </FlexCard>
      <PanelResizeHandle
        panel="agentSidebar"
        anchor={position}
        label="Resize agent panel"
        style={{
          position: 'fixed',
          top: 20,
          height: 'calc(100vh - 120px)',
          [position === 'right' ? 'right' : 'left']: width + 17,
          width: 6,
          transition,
          transform: isOpen
            ? 'translateX(0)'
            : `translateX(${position === 'right' ? '120%' : '-120%'})`,
          zIndex: token.zIndexPopupBase + 2,
          '--panel-grip-color': 'transparent',
          '--panel-grip-color-active': 'transparent',
        }}
      />
      <FlexBox
        {...dropProps}
        style={{
          position: 'fixed',
          bottom: 40,
          right: 20,
          transition,
          transform: isOpen ? 'translateY(0)' : 'translateY(300%)',
          zIndex: token.zIndexPopupBase + 1,
          width: cardWidth,
          maxWidth: panelViewportClamp('agentSidebar'),
        }}
      >
        <ModelInput />
      </FlexBox>
    </>
  );
};

export default Agent;
